/** Серия, задания дня, награда за друга. */
import { Coins, ContentId, PosInt } from "../common";
import { StatModifier } from "./core";
import { z } from "zod";

/** Ежедневная серия: 7 дней, монеты по нарастающей, пропуск дня — сброс на день 1. */
export const StreakDef = z.object({
  /** Награда за день 1…7 (ровно 7 элементов, 7-й — большой мешок). */
  rewards: z.array(Coins).length(7),
  /** Через сколько часов после полуночи UTC начинается «день» серии. */
  dayStartHourUtc: z.number().int().min(0).max(23).default(0),
});
export type StreakDef = z.infer<typeof StreakDef>;

/**
 * Задания дня (ISSUE-49). Цели и награды зависят от лучшей волны игрока на начало дня серии, считает engine.
 * Нет раздела в данных — заданий нет (старые снимки баланса).
 */
export const DailyTasksDef = z.object({
  /** Цель «убей зомби»: столько волн по числу зомби на лучшей волне. */
  killsWaves: PosInt,
  /** Цель «пройди волн»: `base + ⌊лучшая волна / perBestWaves⌋`, не больше `max`. */
  waves: z.object({ base: PosInt, perBestWaves: PosInt, max: PosInt }),
  /** Цель «прокачай»: покупок Level, Stars или Tech за день. */
  upgrades: PosInt,
  /** Награда за задание: столько базовых наград за волну на лучшей волне. */
  rewardWaves: PosInt,
  /** Мешок за все три: столько же, но своим числом. */
  bonusWaves: PosInt,
});
export type DailyTasksDef = z.infer<typeof DailyTasksDef>;

/**
 * Награда за друга (ISSUE-76). Нет раздела в данных — награды нет (старые снимки баланса). Монеты считает
 * `friendRewardCoins` engine от лучшей волны получателя.
 */
export const FriendRewardDef = z.object({
  /** Новичок прошёл эту волну или дальше — награда паре. */
  wave: PosInt,
  /** Награда каждому: столько базовых наград за волну на его лучшей волне. */
  rewardWaves: PosInt,
  /** Наград пригласившему за всё время; сверх них новичок всё равно получает свою. */
  inviterCap: PosInt,
});
export type FriendRewardDef = z.infer<typeof FriendRewardDef>;

/** Разовое усиление из посылки: действует до конца волны, в забежку не входит. */
export const ParcelBoostDef = z.object({
  id: ContentId,
  modifiers: z.array(StatModifier).min(1),
});
export type ParcelBoostDef = z.infer<typeof ParcelBoostDef>;

/** Что лежит в посылке. Монеты — доля базовой награды за волну, сырые: `coinGain` применяет `waveReward` один раз. */
export const ParcelRewardDef = z.discriminatedUnion("type", [
  z.object({ type: z.literal("coins"), weight: PosInt, waveRewardPermille: PosInt, minCoins: PosInt }),
  z.object({ type: z.literal("boost"), weight: PosInt, boostId: ContentId }),
]);
export type ParcelRewardDef = z.infer<typeof ParcelRewardDef>;

/** Посылка в бою (ISSUE-118): расписание и награды. Нет раздела — посылок нет, старые журналы играются как раньше. */
export const ParcelsDef = z.object({
  /** С какой волны появляются. */
  fromWave: PosInt,
  /** Первая посылка волны: тиков от начала боя, от и до. */
  firstMinTicks: PosInt,
  firstMaxTicks: PosInt,
  /** Пауза между посылками, тиков, от и до. */
  gapMinTicks: PosInt,
  gapMaxTicks: PosInt,
  /** Сколько посылка ждёт тапа при скорости x1, тиков. На скорости xN клиент держит её в N раз дольше в тиках. */
  lifeTicks: PosInt,
  /** Предел ожидания в тиках: столько принимает сервер, при наибольшей скорости боя. */
  maxLifeTicks: PosInt,
  rewards: z.array(ParcelRewardDef).min(1),
  boosts: z.array(ParcelBoostDef),
});
export type ParcelsDef = z.infer<typeof ParcelsDef>;
